/**
 * Usage Table Component
 * 
 * Displays promo usages in a table format with:
 * 1. Order Id
 * 2. User (customerEmail)
 * 3. Domain
 * 4. Amount before (originalAmount)
 * 5. Discount (discountValue)
 * 6. Final amount (discountedAmount)
 * 7. Date (usedAt)
 */

import React from 'react';
import { Table } from 'react-bootstrap';
import { PromoUsage } from '../../../../../api/promos/types/usageTypes';
import '../PromoDetail.css';

interface UsageTableProps {
  usages: PromoUsage[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// Format timestamp for display (DD-MM-YYYY only)
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return timestamp;
  }
};

// Format currency
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '-';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const UsageTable: React.FC<UsageTableProps> = ({
  usages,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="usage-table-wrapper">
      <Table responsive className="usage-table">
        <thead>
          <tr>
            <th>Order Id</th>
            <th>User</th>
            <th>Domain</th>
            <th>Amount Before</th>
            <th>Discount</th>
            <th>Final Amount</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {usages.map((usage) => {
            // User is mapped to customerEmail
            const userDisplay = usage.userEmail || usage.userName || usage.userId || '-';
            
            return (
              <tr key={usage._id}>
                {/* 1. Order Id */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {usage.orderId || usage.transactionId || '-'}
                  </span>
                </td>
                
                {/* 2. User (customerEmail) */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {userDisplay}
                  </span>
                </td>
                
                {/* 3. Domain */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {usage.domain || '-'}
                  </span>
                </td>
                
                {/* 4. Amount Before (originalAmount) */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057', fontWeight: 500 }}>
                    {formatCurrency(usage.amount)}
                  </span>
                </td>
                
                {/* 5. Discount (discountValue) */}
                <td>
                  <span style={{ fontSize: '13px', color: '#DC2626', fontWeight: 500 }}>
                    {formatCurrency(usage.discountAmount)}
                  </span>
                </td>
                
                {/* 6. Final Amount (discountedAmount) */}
                <td>
                  <span style={{ fontSize: '13px', color: '#10B981', fontWeight: 500 }}>
                    {formatCurrency(usage.discountedAmount)}
                  </span>
                </td>
                
                {/* 7. Date (usedAt) */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {formatTimestamp(usage.usedAt)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default UsageTable;
